import { isOffsetDateTime, isUuid } from '@/lib/validation';
import { ApiError } from '../http/errors';

export interface PricingValues {
  regular_amount_minor: number | null;
  deposit_amount: number | null;
  cleaning_fee: number | null;
}
export interface PricingFieldSource {
  value: number | null;
  source_policy_id: string | null;
  source_kind?: 'TYPE' | 'ROOM' | 'UNIT' | null;
  revision: number | null;
  reason: 'TYPE_POLICY_ABSENT' | 'LEGACY_UNKNOWN' | null;
}
type Kind = 'TYPE' | 'ROOM';
type Obj = Record<string, unknown>;
const fail = (): never => {
  throw new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '가격 편집 계약을 확인하지 못했습니다.',
  });
};
const obj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const closed = (v: unknown, keys: string): v is Obj =>
  obj(v) && Object.keys(v).sort().join(',') === keys.split(' ').sort().join(',');
const integer = (v: unknown, min = 0): v is number => Number.isSafeInteger(v) && Number(v) >= min;
const uuid = (v: unknown) => typeof v === 'string' && isUuid(v);
const nullableUuid = (v: unknown) => v === null || uuid(v);
const date = (v: unknown) => v === null || isOffsetDateTime(v);
const fields = 'regular_amount_minor deposit_amount cleaning_fee';
const valuesValid = (v: Obj, nullable: boolean) =>
  fields
    .split(' ')
    .every(
      (key) =>
        (nullable && v[key] === null) || integer(v[key], key === 'regular_amount_minor' ? 1 : 0),
    );
const targetValid = (v: unknown, kind: Kind, id: string): v is Obj =>
  closed(v, 'kind property_id type_id room_id structure_revision version') &&
  v.kind === kind &&
  uuid(v.property_id) &&
  uuid(v.type_id) &&
  (kind === 'TYPE' ? v.type_id === id && v.room_id === null : v.room_id === id) &&
  integer(v.structure_revision, 1) &&
  integer(v.version, 1);
const token = (v: unknown, tier = false) =>
  closed(v, `id revision deleted_at${tier ? ' unit min max' : ''}`) &&
  nullableUuid(v.id) &&
  integer(v.revision) &&
  date(v.deleted_at) &&
  (v.id === null ? v.revision === 0 && v.deleted_at === null : v.revision > 0) &&
  (!tier ||
    ((v.unit === null || v.unit === 'MONTH' || v.unit === 'DAY') &&
      (v.min === null || integer(v.min, 1)) &&
      (v.max === null || integer(v.max, 1))));
export function validatePricingCas(v: unknown, kind: Kind, id: string): asserts v is Obj {
  if (
    !closed(v, 'property_id target policies tiers rule_revisions') ||
    !targetValid(v.target, kind, id) ||
    v.property_id !== v.target.property_id ||
    !closed(v.policies, 'TYPE ROOM') ||
    !Object.values(v.policies).every((t) => token(t)) ||
    !closed(v.tiers, 'S M L') ||
    !Object.values(v.tiers).every((t) => token(t, true)) ||
    !obj(v.rule_revisions) ||
    !Object.entries(v.rule_revisions).every(
      ([key, t]) =>
        uuid(key) &&
        closed(t, 'revision deleted_at') &&
        integer(t.revision, 1) &&
        date(t.deleted_at),
    )
  )
    fail();
}
const policyValid = (v: unknown, target: Obj, kind: Kind) =>
  v === null ||
  (closed(
    v,
    `id property_id pricing_policy_code target_kind room_type_id room_id unit_id currency price_basis ${fields} value_semantics revision created_at updated_at deleted_at`,
  ) &&
    uuid(v.id) &&
    v.property_id === target.property_id &&
    v.target_kind === kind &&
    (kind === 'TYPE'
      ? v.room_type_id === target.type_id && v.room_id === null
      : v.room_id === target.room_id && v.room_type_id === null) &&
    v.unit_id === null &&
    typeof v.pricing_policy_code === 'string' &&
    typeof v.currency === 'string' &&
    typeof v.price_basis === 'string' &&
    ['NATIVE_EXPLICIT', 'LEGACY_PRESERVED'].includes(String(v.value_semantics)) &&
    fields.split(' ').every((key) => v[key] === null || integer(v[key])) &&
    integer(v.revision, 1) &&
    isOffsetDateTime(v.created_at) &&
    isOffsetDateTime(v.updated_at) &&
    date(v.deleted_at));
const ruleValid = (v: unknown) =>
  closed(
    v,
    'id price_policy_id term_tier_id applies_to booking_from booking_until calculation_type calculation_value revision created_at updated_at deleted_at',
  ) &&
  uuid(v.id) &&
  uuid(v.price_policy_id) &&
  nullableUuid(v.term_tier_id) &&
  ['NEW', 'EXTENSION'].includes(String(v.applies_to)) &&
  date(v.booking_from) &&
  date(v.booking_until) &&
  ['AMOUNT_DISCOUNT', 'RATE_DISCOUNT', 'FIXED_PRICE'].includes(String(v.calculation_type)) &&
  typeof v.calculation_value === 'string' &&
  integer(v.revision, 1) &&
  isOffsetDateTime(v.created_at) &&
  isOffsetDateTime(v.updated_at) &&
  date(v.deleted_at);
const tierValid = (v: unknown, property: unknown) =>
  closed(
    v,
    'id property_id code duration_unit min_duration max_duration revision created_at updated_at deleted_at',
  ) &&
  uuid(v.id) &&
  v.property_id === property &&
  ['S', 'M', 'L'].includes(String(v.code)) &&
  ['MONTH', 'DAY'].includes(String(v.duration_unit)) &&
  integer(v.min_duration, 1) &&
  integer(v.max_duration, v.min_duration) &&
  integer(v.revision, 1) &&
  isOffsetDateTime(v.created_at) &&
  isOffsetDateTime(v.updated_at) &&
  date(v.deleted_at);
export function decodePricingEdit(v: unknown, kind: Kind, id: string): Obj {
  if (
    !closed(v, `target type_policy ${kind === 'ROOM' ? 'room_policy ' : ''}rules tiers cas`) ||
    !targetValid(v.target, kind, id)
  )
    return fail();
  validatePricingCas(v.cas, kind, id);
  const target = v.target;
  const cas = v.cas;
  if (
    Object.keys(target).some((key) => (cas.target as Obj)[key] !== target[key]) ||
    !policyValid(v.type_policy, v.target, 'TYPE') ||
    (kind === 'ROOM' && !policyValid(v.room_policy, v.target, 'ROOM')) ||
    !Array.isArray(v.tiers) ||
    !v.tiers.every((t) => tierValid(t, target.property_id))
  )
    return fail();
  const rules =
    kind === 'TYPE'
      ? v.rules
      : closed(v.rules, 'type room') && Array.isArray(v.rules.type) && Array.isArray(v.rules.room)
        ? [...(v.rules.type as unknown[]), ...(v.rules.room as unknown[])]
        : null;
  if (!Array.isArray(rules) || !rules.every(ruleValid)) return fail();
  return v;
}
export function decodeRoomPricingRead(v: unknown, id: string): Obj {
  if (!closed(v, 'target type_policy room_policy rules tiers cas effective_fields')) return fail();
  const { effective_fields: effective, ...edit } = v;
  decodePricingEdit(edit, 'ROOM', id);
  if (
    !closed(effective, fields) ||
    !Object.values(effective).every(
      (field) =>
        (closed(field, 'value source_policy_id revision reason') ||
          // 010 계약은 선택 정책의 범위(source_kind)를 함께 내려 준다. 없거나 null/TYPE/ROOM/UNIT만 허용한다.
          (closed(field, 'value source_policy_id source_kind revision reason') &&
            (field.source_kind === null ||
              field.source_kind === 'TYPE' ||
              field.source_kind === 'ROOM' ||
              field.source_kind === 'UNIT'))) &&
        (field.value === null || integer(field.value)) &&
        nullableUuid(field.source_policy_id) &&
        (field.revision === null || integer(field.revision, 1)) &&
        (field.reason === null ||
          field.reason === 'TYPE_POLICY_ABSENT' ||
          field.reason === 'LEGACY_UNKNOWN'),
    )
  )
    return fail();
  return v;
}
export function validatePricingInput(
  v: { command_id: string; expected: Obj; values: PricingValues; reason: string },
  kind: Kind,
  id: string,
) {
  if (
    !uuid(id) ||
    !uuid(v.command_id) ||
    !v.reason.trim() ||
    v.reason.length > 500 ||
    !closed(v.values, fields) ||
    !valuesValid(v.values, kind === 'ROOM')
  )
    fail();
  validatePricingCas(v.expected, kind, id);
}
export function decodePricingCommand(v: unknown, kind: Kind, id: string, command: string): Obj {
  if (
    !closed(
      v,
      'command_id outcome target_id policy_id rule_id result_revision first_result current_snapshot_at_first_execution',
    ) ||
    v.command_id !== command ||
    v.target_id !== v.policy_id ||
    !['CHANGED', 'NO_CHANGE'].includes(String(v.outcome)) ||
    !uuid(v.policy_id) ||
    v.rule_id !== null ||
    !integer(v.result_revision, 1) ||
    !closed(v.first_result, 'schema_version request_fingerprint received_at') ||
    v.first_result.schema_version !== 1 ||
    typeof v.first_result.request_fingerprint !== 'string' ||
    !/^[a-f0-9]{64}$/.test(v.first_result.request_fingerprint) ||
    !isOffsetDateTime(v.first_result.received_at)
  )
    return fail();
  const snapshot = decodePricingEdit(v.current_snapshot_at_first_execution, kind, id);
  const policy = snapshot[kind === 'TYPE' ? 'type_policy' : 'room_policy'];
  if (!obj(policy) || policy.id !== v.policy_id || policy.revision !== v.result_revision)
    return fail();
  return v;
}
